import { useMemo, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCaseDataContext } from '../CaseDataContext';
import { useDatabase } from './useDatabase';

/**
 * Every live-query emission decrypts brand-new juror objects, even for
 * jurors that did not change. Downstream, those fresh references defeat
 * React.memo and force every juror card to re-render on every write.
 *
 * This hook reuses the previous object for any juror whose change-tracking
 * fields (updated, revision, seat assignments) are identical, and reuses
 * the previous array when no juror changed at all. Every local write path
 * in JurorRepository bumps `updated`, and remote applies carry the server's
 * `updated`/`revision`, so these fields are a reliable change signal.
 */
function useStableJurors(jurors) {
  const cacheRef = useRef({ byId: new Map(), array: [] });

  return useMemo(() => {
    if (!jurors) {
      return cacheRef.current.array;
    }

    const prev = cacheRef.current;
    const nextById = new Map();
    let unchanged = jurors.length === prev.array.length;

    const next = jurors.map((juror, idx) => {
      const old = prev.byId.get(juror._id);
      const reusable =
        old &&
        old.updated === juror.updated &&
        old.revision === juror.revision &&
        old.seatNumber === juror.seatNumber &&
        old.alternateSeatNumber === juror.alternateSeatNumber;

      const out = reusable ? old : juror;
      if (out !== prev.array[idx]) {
        unchanged = false;
      }
      nextById.set(juror._id, out);
      return out;
    });

    const result = unchanged ? prev.array : next;
    cacheRef.current = { byId: nextById, array: result };
    return result;
  }, [jurors]);
}

/**
 * Same idea for the case record: the combined page query re-runs on juror
 * writes and returns a fresh caseData object each time even when the case
 * itself didn't change.
 */
function useStableCaseData(caseData) {
  const prevRef = useRef(null);

  return useMemo(() => {
    if (!caseData) {
      prevRef.current = null;
      return null;
    }

    const prev = prevRef.current;
    if (
      prev &&
      prev._id === caseData._id &&
      prev.revision === caseData.revision &&
      prev.updatedAt === caseData.updatedAt
    ) {
      return prev;
    }

    prevRef.current = caseData;
    return caseData;
  }, [caseData]);
}

/**
 * Hook to get all cases with live updates
 * Re-renders automatically when cases change in IndexedDB
 * 
 * @returns {{ cases: Array, isLoading: boolean }}
 */
export function useCases() {
  const { db } = useDatabase();
  
  const cases = useLiveQuery(
    async () => {
      if (!db) return [];
      const cases = await db.cases.toArray();
      return cases.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
    },
    [db],
    [] // default value while loading
  );

  return {
    cases: cases || [],
    isLoading: cases === undefined
  };
}

/**
 * Hook to get a single case by ID with live updates
 * 
 * @param {string} caseId - Case ID
 * @returns {{ caseData: Object|null, isLoading: boolean, exists: boolean }}
 */
export function useCase(caseId) {
  const ctx = useCaseDataContext();
  const { db } = useDatabase();
  
  const liveCaseData = useLiveQuery(
    async () => {
      if (!db || !caseId) return null;
      return db.cases.get(caseId);
    },
    [db, caseId],
    undefined // undefined while loading, null if not found
  );

  const caseData =
    liveCaseData !== undefined
      ? liveCaseData
      : ctx?.caseId === caseId
        ? ctx.caseData
        : undefined;

  return {
    caseData: caseData || null,
    isLoading: caseData === undefined,
    exists: caseData !== null && caseData !== undefined
  };
}

/**
 * Hook to know when local Dexie data for a case page is ready to render.
 * Loads case metadata and jurors in one query so the UI can avoid empty
 * placeholder flashes while useLiveQuery hooks warm up.
 *
 * A case created offline is first stored under a local UUID, then swapped
 * to the server-assigned id once sync completes (SyncEngine.updateLocalCaseIdToServerId
 * deletes the local-id record and re-inserts it under the server id, stamped
 * with `_localId` so the old id can still be traced). If the current caseId
 * no longer resolves, this looks for that trace and surfaces it as
 * `redirectTo` so the page can follow the case to its new address instead of
 * rendering an empty/default state.
 *
 * @param {string} caseId - Case ID
 * @returns {{ caseData: Object|null, jurors: Array, isLoading: boolean, redirectTo: string|null }}
 */
export function useCasePageReady(caseId) {
  const { db, isReady: dbReady } = useDatabase();

  const data = useLiveQuery(
    async () => {
      if (!db || !caseId) {
        return null;
      }

      const caseData = await db.cases.get(caseId);

      if (!caseData) {
        const remapped = await db.cases.filter((c) => c._localId === caseId).first();
        if (remapped) {
          return { caseData: null, jurors: [], redirectTo: remapped._id };
        }
        return { caseData: null, jurors: [], redirectTo: null };
      }

      const jurors = await db.jurors.where('caseId').equals(caseId).toArray();
      return { caseData, jurors, redirectTo: null };
    },
    [db, caseId],
    undefined,
  );

  const caseData = useStableCaseData(data?.caseData ?? null);
  const jurors = useStableJurors(data?.jurors);
  const redirectTo = data?.redirectTo ?? null;

  return {
    caseData,
    jurors,
    redirectTo,
    isLoading:
      !dbReady ||
      !caseId ||
      data === undefined ||
      !!redirectTo ||
      (data.caseData != null && data.caseData._id !== caseId),
  };
}

/**
 * Hook to get case with computed properties
 * Includes juror counts and other derived data
 * 
 * @param {string} caseId - Case ID
 * @returns {{ caseData: Object|null, jurorCount: number, isLoading: boolean }}
 */
export function useCaseWithStats(caseId) {
  const { db } = useDatabase();
  
  const result = useLiveQuery(
    async () => {
      if (!db || !caseId) return { caseData: null, jurorCount: 0 };
      
      const caseData = await db.cases.get(caseId);
      if (!caseData) return { caseData: null, jurorCount: 0 };
      
      const jurorCount = await db.jurors.where('caseId').equals(caseId).count();
      
      return { caseData, jurorCount };
    },
    [db, caseId],
    undefined
  );

  return {
    caseData: result?.caseData || null,
    jurorCount: result?.jurorCount || 0,
    isLoading: result === undefined
  };
}

/**
 * Hook to get cases filtered by creator
 * 
 * @param {string} creatorEmail - Creator's email
 * @returns {{ cases: Array, isLoading: boolean }}
 */
export function useCasesByCreator(creatorEmail) {
  const { db } = useDatabase();
  
  const cases = useLiveQuery(
    async () => {
      if (!db || !creatorEmail) return [];
      return db.cases.where('creator').equals(creatorEmail).toArray();
    },
    [db, creatorEmail],
    []
  );

  return {
    cases: cases || [],
    isLoading: cases === undefined
  };
}

/**
 * Hook to check if a case has local (unsynced) changes
 * 
 * @param {string} caseId - Case ID
 * @returns {{ hasChanges: boolean, isLoading: boolean }}
 */
export function useCaseHasLocalChanges(caseId) {
  const { db } = useDatabase();
  
  const hasChanges = useLiveQuery(
    async () => {
      if (!db || !caseId) return false;
      
      const caseData = await db.cases.get(caseId);
      if (!caseData) return false;
      if (caseData._localOnly) return true;
      
      // Check pending operations
      const pendingOp = await db.pendingOperations
        .where({ entityType: 'case', entityId: caseId })
        .first();
      
      return !!pendingOp;
    },
    [db, caseId],
    false
  );

  return {
    hasChanges: hasChanges || false,
    isLoading: hasChanges === undefined
  };
}

// Export individual hooks
export default {
  useCases,
  useCase,
  useCaseWithStats,
  useCasesByCreator,
  useCaseHasLocalChanges,
  useCasePageReady,
};
